<template>
	<div>
		<!-- This is CMS Component -->
		<div class="flex wrap xs-12">
			<!-- Form -->
			<div class="flex wrap xs-12">
				<!-- Form -->
				<div class="xs-12 md-7 p8">
					<products-liability-quote />
				</div>

				<!-- Right column -->
				<div
					class="xs-12 md-4 bg4 p4"
					style="height:90vh; overflow-y:auto; position:sticky; top:16px;"
				>
					<berkley-proposal-status />
					<berkley-premium class="mt6" />
					<berkley-files
						class="mt6"
						:files="computedFiles"
						@file:deleted="handleRefetch"
						@file:uploaded="handleRefetch"
					/>
					<berkley-actions class="mt6" @proposal:issued="handleRefetch" />
				</div>
			</div>
		</div>

		<!-- God mode switcher -->
		<div
			style="position:fixed; bottom:0; right:0; z-index:100;"
			class="xs-12 p1 ta3 bg4"
			@click="
				$store.commit(
					'caseManagement/SET_GOD_MODE',
					!$store.state.caseManagement.isGodMode
				)
			"
		>
			<span class="tt2 fs2 c6 fw7 cu2">
				God Mode is
				{{ $store.state.caseManagement.isGodMode === true ? "On" : "Off" }}
			</span>
		</div>
	</div>
</template>

<script>
/**
 * What we are doing in this case template:
 * 1 When this component is mounted, it's checking if we have proposal and offer, if we don't have, we create it
 * 2 This component has wathcers
 *  - we are checking if case facts changed and we save values and refetch data to update premium, we also get premiumCurrency from case.facts to display premium in that currency
 *  - we are checking if some specific benefitOptions changed abd if so, we save values and refetch data to refresh premium, we also get premiumCurrency from case.facts to display premium in that currency
 * 3 We have a special method that composes values for pricing, it gathers data across the store and converts it into scalarValues for pricing
 *  - In this method we also do conversion of currencies as pricing engine is done in USD, we have to normalize data into USD currency, we use currency converter API
 * 4 We have case.facts.currency which is contract currency (premium in PDFs will be displayed in that currency); We also have case.facts.premiumCurrency which is a helper that says in which currency we are showing the premium and commission split
 * 5 When adding some endorsements, we are also autoamtically adding loadings. When removing endorsements, we are removing loadings that were added. This logic is happening in the component handling endorsements
 */
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import debounce from "lodash.debounce"

// CMS
import BerkleyPremium from "../mockedBerkley/BerkleyPremium.vue"
import BerkleyActions from "./BerkleyActions"
import BerkleyFiles from "../mockedBerkley/BerkleyFiles.vue"
import BerkleyProposalStatus from "../mockedBerkley/BerkleyProposalStatus.vue"
import ProductsLiabilityQuote from "../mockedBerkley/ProductsLiabilityQuote"

export default {
	components: {
		// Should come from CMS
		BerkleyPremium,
		BerkleyActions,
		BerkleyFiles,
		BerkleyProposalStatus,
		ProductsLiabilityQuote
	},

	data() {
		return {
			files: []
		}
	},

	methods: {
		/**
		 * When there is no proposal and offer, create them
		 */
		async init() {
			this.isLoading = true
			const proposal = this.$store.state.caseManagement?.proposals?.[0]
			const offer = this.$store.state.caseManagement?.proposals?.[0]
				?.basket?.[0]

			// Check store if we have at least one proposal
			if (!proposal) {
				const res = await this.addProposal()
				this.$store.commit(
					"caseManagement/SET_PROPOSAL_ID",
					res?.data?.addProposal?.createdStatus?.id
				)
			} else this.$store.commit("caseManagement/SET_PROPOSAL_ID", proposal?.id)

			// Check if we have offer
			if (!offer) {
				const res = await this.addOffer()
				this.$store.commit(
					"caseManagement/SET_OFFER_ID",
					res?.data?.addOfferToProposal?.createdStatus?.id
				)
			} else this.$store.commit("caseManagement/SET_OFFER_ID", offer?.id)

			// Find case currencyCode in case facts
			const caseCurrencyCode = this.$store.state.caseManagement.case?.facts?.find(
				fact => fact.type === "premiumCurrency"
			)?.value?.stringValue

			// Refetch store so that all dependant components can get the latest updated data
			await this.$store.dispatch("caseManagement/fetchProposals", {
				caseId: this.$store.state.caseManagement.caseId,
				options: { currencyCodes: [caseCurrencyCode] }
			})

			this.isLoading = false
		},

		async addProposal() {
			const query = gql`
				mutation addProposal($caseId: String!, $input: addProposalInput!) {
					addProposal(caseId: $caseId, input: $input) {
						status
						errors
						createdStatus {
							id
						}
					}
				}
			`

			const res = await api({
				query,
				variables: {
					caseId: this.$store.state.caseManagement.caseId,
					input: {}
				}
			})

			// Errors
			if (res?.data?.addProposal?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.addProposal?.errors?.[0] || this.$t("errors.general")
				)
				return Promise.resolve(res)
			}

			return Promise.resolve(res)
		},

		async addOffer() {
			const query = gql`
				mutation addOfferToProposal(
					$caseId: String!
					$proposalId: String!
					$input: addOfferInput!
				) {
					addOfferToProposal(
						caseId: $caseId
						proposalId: $proposalId
						input: $input
					) {
						status
						errors
						createdStatus {
							id
						}
					}
				}
			`

			const variables = {
				caseId: this.$store.state.caseManagement.caseId,
				proposalId: this.$store.state.caseManagement.proposalId,
				input: {
					productId: {
						plan: "berkleyAsia_product_liability",
						type: "product_liability"
					}
				}
			}

			// Call
			const res = await api({
				query,
				variables
			})

			// Errors
			if (res?.data?.addOfferToProposal?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.addOfferToProposal?.errors?.[0] ||
						this.$t("errors.general")
				)
				return Promise.resolve(res)
			}

			return Promise.resolve(res)
		},

		async updateOffer(input) {
			const query = gql`
				mutation updateOfferOfProposal(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$input: updateOfferInput!
				) {
					updateOfferOfProposal(
						caseId: $caseId
						proposalId: $proposalId
						offerId: $offerId
						updateOfferInput: $input
					) {
						errors
						status
					}
				}
			`
			const { caseId, proposalId, offerId } = this.$store.state.caseManagement

			const res = await api({
				query,
				variables: {
					caseId,
					proposalId,
					offerId,
					input
				}
			})

			return Promise.resolve(res)
		},

		/**
		 * This method will update values in selected offer
		 * and it will also refetch premium and update store cache
		 * It's being invoked in watchers
		 */
		updatePremium: debounce(async function() {
			// Construct values based on facts
			const values = await this.constructValues()

			// Update offer values
			const res = await this.updateOffer({ values })

			// Check errors
			// TODO:

			// Find case premiumCurrency in case facts
			const caseCurrencyCode = this.$store.state.caseManagement.case?.facts?.find(
				fact => fact.type === "premiumCurrency"
			)?.value?.stringValue

			// Refetch store so that all dependant components can get the latest updated data
			this.$store.dispatch("caseManagement/fetchProposals", {
				caseId: this.$store.state.caseManagement.caseId,
				options: { currencyCodes: [caseCurrencyCode] }
			})
		}, 500),

		/**
		 * Some benefitOptions should trigger premium updates
		 */
		shouldUpdatePremiumBasedOnBenefitOptions(newValue, oldValue) {
			// We are interested only in a specific benefitOption
			// We are interested only in benefit options for selcted proposal and selected offer (in store it is offerId, proposalId)
			// We have to go to oldValue and newValue and find out if that specific benefitOption changed
			// If so, we updatePremium
			// If not, we do nothing
			const oldBenefitOptionValue = this.getBenefitOption(
				oldValue,
				"product_liability"
			)?.value?.numberValue

			const newBenefitOptionValue = this.getBenefitOption(
				newValue,
				"product_liability"
			)?.value?.numberValue

			if (oldBenefitOptionValue !== newBenefitOptionValue) this.updatePremium()
		},

		/**
		 * Helper function to get all values from store and construct object that API is expecting
		 * It will also call currencyExchange API to convert currencies into USD
		 */
		async constructValues() {
			const query = gql`
				query convertValuesCurrencies(
					$prices: [priceInput]!
					$options: exchangeRateConversionOptionsInput!
				) {
					exchangeRateConvert(prices: $prices, options: $options) {
						amount
						currencyCode
						formattedPrice
					}
				}
			`

			/**
			 * Helper function to get RiskHazardGrade from NAIC code
			 */
			const getRiskHazardGrade = cache => {
				const refId = cache?.facts?.find(
					fact => fact.type === "insured0NaicCodeId"
				)?.value?.stringValue

				const value = cache?.insureds?.[0]?.facts
					?.find(fact => fact.id === refId)
					?.value?.objectValue?.find(item => item.key === "riskHazardGrade")
					?.value?.numberValue

				return value
			}

			/**
			 * Helper function to get turnovers
			 * It is saved as a case fact reference
			 * Data should be retrieved from holder
			 */
			const getTurnover = (caseFactType = "", cache, what = "amount") => {
				if (!caseFactType) return null

				// Ref id
				const factId = cache?.facts?.find(fact => fact.type === caseFactType)
					?.value?.stringValue

				// Referenced value
				const objectValue = cache?.holder?.facts?.find(
					fact => fact.id === factId
				)?.value?.objectValue

				// Amount
				if (what === "amount")
					return objectValue?.find(obj => obj.key === "amount")?.value
						?.numberValue

				// Currency
				if (what === "currency") {
					return objectValue?.find(obj => obj.key === "currency")?.value
						?.stringValue
				}
			}

			/**
			 * It will return liability
			 */
			const getLiability = () => {
				return this.getBenefitOption(
					this.$store.state.caseManagement.proposals,
					"product_liability"
				)?.value?.numberValue
			}

			/**
			 * Find case currencyCode in case.facts
			 */
			const caseCurrencyCode = this.$store.state.caseManagement.case?.facts?.find(
				fact => fact.type === "currency"
			)?.value?.stringValue

			// Get store state for convenience
			const cache = this.$store.state.caseManagement.case

			// Prepare all numbers that we will need in values
			const notConvertedValues = {
				asiaTurnoverInUSD: {
					amount: getTurnover("holderTurnoverInAsiaId", cache),
					currency: getTurnover("holderTurnoverInAsiaId", cache, "currency")
				},
				australiaOrNewZealandTurnoverInUSD: {
					amount: getTurnover("holderTurnoverInAustraliaId", cache),
					currency: getTurnover(
						"holderTurnoverInAustraliaId",
						cache,
						"currency"
					)
				},
				europeTurnoverInUSD: {
					amount: getTurnover("holderTurnoverInEuropeId", cache),
					currency: getTurnover("holderTurnoverInEuropeId", cache, "currency")
				},
				northAmericaTurnoverInUSD: {
					amount: getTurnover("holderTurnoverInAmericaId", cache),
					currency: getTurnover("holderTurnoverInAmericaId", cache, "currency")
				},
				restOfTheWorldTurnoverInUSD: {
					amount: getTurnover("holderTurnoverInWorldId", cache),
					currency: getTurnover("holderTurnoverInWorldId", cache, "currency")
				},
				limitOfLiabilityInUSD: {
					amount: getLiability(),
					currency: caseCurrencyCode
				}
			}

			// Call API to convert currencies
			const convertedValuesRes = await api({
				query,
				variables: {
					prices: Object.keys(notConvertedValues).map(key => ({
						amount: notConvertedValues?.[key]?.amount || 0,
						currencyCode: notConvertedValues?.[key]?.currency || "USD"
					})),
					options: {
						currencyCodes: ["USD"]
					}
				}
			})

			// Build object with converted currencies
			const convertedValues = {}
			Object.keys(notConvertedValues).forEach((key, index) => {
				convertedValues[key] = {
					amount:
						convertedValuesRes?.data?.exchangeRateConvert?.[index]?.amount || 0
				}
			})

			// Construct output
			const values = [
				{
					key: "riskHazardGrade",
					value: { numberValue: getRiskHazardGrade(cache) }
				},
				{
					key: "insureds",
					value: {
						arrayValue: [
							{
								objectValue: [
									{
										key: "asiaTurnoverInUSD",
										value: {
											numberValue: convertedValues.asiaTurnoverInUSD.amount
										}
									},
									{
										key: "australiaOrNewZealandTurnoverInUSD",
										value: {
											numberValue:
												convertedValues.australiaOrNewZealandTurnoverInUSD
													.amount
										}
									},
									{
										key: "europeTurnoverInUSD",
										value: {
											numberValue: convertedValues.europeTurnoverInUSD.amount
										}
									},
									{
										key: "northAmericaTurnoverInUSD",
										value: {
											numberValue:
												convertedValues.northAmericaTurnoverInUSD.amount
										}
									},
									{
										key: "restOfTheWorldTurnoverInUSD",
										value: {
											numberValue:
												convertedValues.restOfTheWorldTurnoverInUSD.amount
										}
									},
									{
										key: "limitOfLiabilityInUSD",
										value: {
											numberValue: convertedValues.limitOfLiabilityInUSD.amount
										}
									}
								]
							}
						]
					}
				}
			]
			return Promise.resolve(values)
		},

		/**
		 * Helper function to find benefitOption in store
		 */
		getBenefitOption(value, benefitTypeId) {
			const { proposalId, offerId } = this.$store.state.caseManagement
			const proposal = value?.find(proposal => proposal.id === proposalId)
			const offer = proposal?.basket?.find(offer => offer.id === offerId)

			const foundOption = offer?.benefitOptions?.find(
				option => option.benefitTypeId === benefitTypeId
			)
			return foundOption
		},

		/** fetch files attached to this proposal */
		async fetchFiles() {
			const query = gql`
				query fileListing($prefix: String) {
					list: fileListing(prefix: $prefix) {
						objectSummaries {
							fileName
							key
						}
						commonPrefixes
					}
				}
			`

			const res = await api({
				query,
				variables: {
					prefix: `proposals/${this.$store.state.caseManagement.proposalId}/`
				}
			})

			this.files = res?.data?.list?.objectSummaries
		},

		handleRefetch() {
			this.init()
			this.fetchFiles()
		}
	},

	computed: {
		computedFiles() {
			return this.files
		}
	},

	mounted() {
		this.init()
	},

	// Watch for specific things in store to refresh premium
	watch: {
		"$store.state.caseManagement.case.facts": {
			handler() {
				this.updatePremium()
			},
			deep: true
		},

		"$store.state.caseManagement.proposals": {
			handler(newValue, oldValue) {
				this.shouldUpdatePremiumBasedOnBenefitOptions(newValue, oldValue)
			},
			deep: true
		},

		"$store.state.caseManagement.proposalId": {
			handler() {
				this.fetchFiles()
			}
		}
	}
}
</script>
